import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { extractErrorMessage } from '../api/errorUtils';
import axiosInstance from '../api/axiosInstance';
import { showNotification } from './notificationSlice';

const ENDPOINTS = {
    getRecipe: 'techsavanna_pos.api.recipe.get_recipe',
    createRecipe: 'techsavanna_pos.api.recipe.create_recipe',
};

const extractResponseData = (response) => {
    if (response?.data?.success && response?.data?.data) return response.data.data;
    if (response?.data?.message?.data) return response.data.message.data;
    if (typeof response?.data?.message !== 'undefined') return response.data.message;
    return response?.data;
};


// ─── Async Thunks ──────────────────────────────────────────────────────────────

export const fetchRecipe = createAsyncThunk(
    'recipe/fetchRecipe',
    async ({ itemCode }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(ENDPOINTS.getRecipe, {
                params: { item_code: itemCode },
            });
            return extractResponseData(response);
        } catch (error) {
            const message = extractErrorMessage(error);
            dispatch(showNotification({ message, severity: 'error', title: 'Failed to fetch recipe' }));
            return rejectWithValue(message);
        }
    }
);

export const saveRecipe = createAsyncThunk(
    'recipe/saveRecipe',
    async ({ itemCode, materials, yieldQty = 1, outputUom = null }, { dispatch, rejectWithValue }) => {
        try {
            const payload = {
                item_code: itemCode,
                materials: JSON.stringify(materials),
                yield_qty: yieldQty,
            };
            if (outputUom) payload.output_uom = outputUom;
            const response = await axiosInstance.post(ENDPOINTS.createRecipe, payload);
            const data = extractResponseData(response);
            dispatch(showNotification({
                message: 'Recipe saved successfully',
                severity: 'success',
                title: 'Success',
            }));
            return data;
        } catch (error) {
            const message = extractErrorMessage(error);
            dispatch(showNotification({ message, severity: 'error', title: 'Failed to save recipe' }));
            return rejectWithValue(message);
        }
    }
);

// ─── Slice ─────────────────────────────────────────────────────────────────────

const initialState = {
    current: null,       // { item_code, has_recipe, bom_name, materials[] }
    isLoading: false,
    isSaving: false,
    error: null,
};

const recipeSlice = createSlice({
    name: 'recipe',
    initialState,
    reducers: {
        clearRecipe: (state) => {
            state.current = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchRecipe
            .addCase(fetchRecipe.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.current = null;
            })
            .addCase(fetchRecipe.fulfilled, (state, action) => {
                state.isLoading = false;
                state.current = action.payload;
            })
            .addCase(fetchRecipe.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // saveRecipe
            .addCase(saveRecipe.pending, (state) => {
                state.isSaving = true;
                state.error = null;
            })
            .addCase(saveRecipe.fulfilled, (state, action) => {
                state.isSaving = false;
                // After saving, update current to reflect the newly created BOM
                if (state.current) {
                    state.current.bom_name = action.payload?.name || state.current.bom_name;
                    state.current.has_recipe = true;
                }
            })
            .addCase(saveRecipe.rejected, (state, action) => {
                state.isSaving = false;
                state.error = action.payload;
            });
    },
});

export const { clearRecipe } = recipeSlice.actions;
export default recipeSlice.reducer;
